import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router";
import { useAuth } from "../lib/auth-context.js";
import { usePreferences, type Theme } from "../lib/preferences.js";
import { AI_CHAT_AVAILABLE } from "../lib/feature-flags.js";
import { AiButton } from "./ai-button.js";
import { AlertsBadge } from "./alerts-badge.js";
import { AppShellSettingsTray } from "./app-shell-settings-tray.js";
import { CommandKPalette } from "./command-k-palette.js";

/**
 * Hearth app shell — sticky left sidebar + main content + identity footer.
 *
 * Phase 13.5 (IA implementation, 2026-05-09 IA review block in
 * DECISIONS.md) restructures the sidebar from 8 ad-hoc groups to 7
 * mental-model groups: OVERVIEW / WEALTH / FIRE / TAX / CASH FLOW /
 * STEWARDSHIP / FAMILY. AI is dropped from the sidebar — kept as the
 * floating "Ask Hearth" button + a Cmd-K palette + the standalone /ai
 * page. Operational/configuration entries (Email admin, AI settings,
 * Integrations, Staging inbox, Alerts) move to a footer-anchored
 * Settings tray; see `<AppShellSettingsTray />`. Hash-anchor sidebar
 * entries (`/dashboard#fire`, `/dashboard#stress-test`) are gone — the
 * FIRE numbers + Withdrawal modeler now live as standalone /fire/* routes.
 *
 * Responsive:
 *   - <md (mobile / small tablet): off-canvas drawer, opened via the
 *     hamburger button in the top bar; backdrop closes on click.
 *   - ≥md: sticky sidebar at 256px, always visible.
 *
 * Identity footer:
 *   - Theme toggle (light ↔ dark — first-class dual theme).
 *   - Privacy toggle (mask dollar values).
 *   - Simple View toggle.
 *   - Settings tray + Sign out.
 *
 * Pages outside the shell: /signin, /signup.
 */

interface NavLeaf {
  readonly label: string;
  readonly to?: string;
  readonly comingPhase?: string;
}
interface NavGroup {
  readonly label: string;
  readonly icon: ReactNode;
  readonly items: readonly NavLeaf[];
}

// Inline minimal SVGs — no icon library (the prompt explicitly resists adding
// one). Stroke-currentColor lets them inherit the active text color.
const Ic = {
  home: (
    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12 12 4l9 8" />
      <path d="M5 10v10h14V10" />
    </svg>
  ),
  wallet: (
    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M16 12h3" />
    </svg>
  ),
  flow: (
    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7h12l-3-3M3 7l3 3M21 17H9l3 3M21 17l-3-3" />
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6Z" />
    </svg>
  ),
  family: (
    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20c0-3 3-5 6-5s6 2 6 5" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M14 20c.4-2 2-3 3-3s2.6 1 3 3" />
    </svg>
  ),
  // Phase 13.5: FIRE-elevation icon — flame-on-pedestal for the standalone group.
  flame: (
    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3c1.5 3 4 4.5 4 8a4 4 0 1 1-8 0c0-2 1-2.5 1-4 1 1 2 1 3-4Z" />
    </svg>
  ),
  // Phase 13.5: TAX gets a calculator/document hybrid icon distinct from STEWARDSHIP's archive.
  receipt: (
    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 3h11l3 3v15l-3-2-2 2-2-2-2 2-2-2-3 2Z" />
      <path d="M9 8h6M9 12h6M9 16h4" />
    </svg>
  ),
};

// Phase 13.5: 7-group structure locked in DECISIONS.md 2026-05-09 (IA review):
//   OVERVIEW / WEALTH / FIRE / TAX / CASH FLOW / STEWARDSHIP / FAMILY.
// AI dropped from sidebar (kept as floating button + Cmd-K + standalone /ai).
// Settings tray lives in IdentityFooter — Email admin / AI settings /
// Integrations / Staging inbox / Alerts / Household profile.
const NAV: readonly NavGroup[] = [
  {
    label: "Overview",
    icon: Ic.home,
    items: [{ label: "Dashboard", to: "/dashboard" }],
  },
  {
    label: "Wealth",
    icon: Ic.wallet,
    items: [
      { label: "Accounts", to: "/accounts" },
      { label: "Portfolio", to: "/portfolio" },
      { label: "Watchlists", to: "/watchlists" },
      { label: "Cards", to: "/cards" },
      { label: "Banking", to: "/banking" },
      { label: "Credit", to: "/credit" },
      { label: "Housing", to: "/housing" },
    ],
  },
  {
    label: "FIRE",
    icon: Ic.flame,
    items: [
      { label: "FIRE hub", to: "/fire" },
      { label: "Numbers", to: "/fire/numbers" },
      { label: "Withdrawal", to: "/fire/withdrawal" },
      { label: "Saving rate", to: "/fire/saving-rate" },
      { label: "Contribution priority", to: "/fire/contribution-priority" },
      { label: "Scenarios", to: "/scenarios" },
      { label: "Social Security", to: "/fire/social-security" },
      { label: "Geographic arbitrage", to: "/fire/geographic-arbitrage" },
    ],
  },
  {
    label: "Tax",
    icon: Ic.receipt,
    items: [
      { label: "Tax hub", to: "/tax" },
      { label: "Quarterly", to: "/tax/quarterly" },
      { label: "Capital gains", to: "/tax/capital-gains" },
      { label: "Roth ladder", to: "/tax/roth-ladder" },
      { label: "SEPP", to: "/tax/sepp" },
      { label: "ACA planner", to: "/tax/aca-planner" },
      { label: "Documents", to: "/tax/documents" },
    ],
  },
  {
    label: "Cash flow",
    icon: Ic.flow,
    items: [
      { label: "Bills", to: "/bills" },
      { label: "Spending", to: "/spending" },
      { label: "Income", to: "/income" },
      { label: "Equity grants", to: "/equity-grants" },
    ],
  },
  {
    label: "Stewardship",
    icon: Ic.shield,
    items: [
      { label: "Insurance", to: "/insurance" },
      { label: "Estate", to: "/estate" },
      { label: "Charity", to: "/charity" },
      { label: "Documents", to: "/documents" },
      { label: "Audit log", comingPhase: "Phase 15" },
    ],
  },
  {
    label: "Family",
    icon: Ic.family,
    items: [
      { label: "Members", to: "/family" },
      { label: "Messages", to: "/messages" },
      { label: "College plans", to: "/college-plans" },
      { label: "Journal", to: "/journal" },
    ],
  },
];

export function AppShell({ children }: { children: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Close the mobile drawer on every route change so the user lands on the
  // new page with a clean overlay-free view.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[var(--color-page)] text-[var(--color-text)]">
      {/* Mobile top bar — only visible <md. The hamburger is the entry point
          to the off-canvas drawer; brand mark mirrors the desktop sidebar. */}
      <header className="flex h-14 items-center gap-3 border-b border-[var(--color-border-default)] bg-[var(--color-surface)] px-4 md:hidden">
        <button
          type="button"
          onClick={() => setDrawerOpen((o) => !o)}
          aria-label={drawerOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={drawerOpen}
          className="rounded-md p-2 hover:bg-[var(--color-elevated)]"
        >
          <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </button>
        <Link to="/dashboard" className="font-display text-xl font-medium tracking-tight">
          Hearth
        </Link>
        {/* Phase 13.5: Alerts dropped from sidebar; badge preserves
            discoverability. */}
        <div className="ml-auto">
          <AlertsBadge />
        </div>
      </header>

      <div className="md:flex">
        {/* Sidebar — desktop sticky, mobile off-canvas drawer. */}
        <Sidebar open={drawerOpen} onClose={() => setDrawerOpen(false)} />

        {/* Backdrop for the mobile drawer. */}
        {drawerOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/40 md:hidden"
            onClick={() => setDrawerOpen(false)}
            aria-hidden
          />
        )}

        {/* Main content — flex-1 fills the remaining width on md+. */}
        <main className="min-w-0 flex-1">
          {children}
        </main>
      </div>

      {/* Floating "Ask Hearth" button — Phase 8.1. Hidden on /ai* and
          when AI isn't deployed yet. */}
      {AI_CHAT_AVAILABLE && <AiButton />}

      {/* Phase 13.5: Cmd-K palette mounted globally so the keyboard
          shortcut works from any authed page. AI was dropped from the
          sidebar in this phase; floating button + Cmd-K + the standalone
          /ai page are how users reach AI now. */}
      {AI_CHAT_AVAILABLE && <CommandKPalette />}
    </div>
  );
}

function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <aside
      className={[
        // Desktop: sticky 256px column.
        "md:sticky md:top-0 md:h-screen md:w-64 md:flex md:shrink-0",
        // Mobile: fixed off-canvas drawer; transform-translates in/out.
        "fixed inset-y-0 left-0 z-40 w-72 transform transition-transform md:transform-none md:transition-none",
        open ? "translate-x-0" : "-translate-x-full md:translate-x-0",
        "flex flex-col border-r border-[var(--color-border-default)] bg-[var(--color-surface)]",
      ].join(" ")}
      aria-label="Primary"
    >
      {/* Brand mark — only at md+ since mobile already shows it in the topbar. */}
      <div className="hidden md:flex h-14 items-center gap-2 border-b border-[var(--color-border-default)] px-5">
        <BrandMark />
        <span className="font-display text-xl font-medium tracking-tight">Hearth</span>
        {/* Phase 13.5: Alerts badge — replaces the dropped sidebar entry. */}
        <div className="ml-auto">
          <AlertsBadge />
        </div>
      </div>

      {/* Mobile-only close affordance for the drawer. */}
      <div className="md:hidden flex h-14 items-center justify-between border-b border-[var(--color-border-default)] px-4">
        <Link to="/dashboard" className="flex items-center gap-2">
          <BrandMark />
          <span className="font-display text-xl font-medium tracking-tight">Hearth</span>
        </Link>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-2 hover:bg-[var(--color-elevated)]"
          aria-label="Close navigation"
        >
          <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <path d="M6 6 18 18M6 18 18 6" />
          </svg>
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {NAV.map((group) => (
          <NavGroupBlock key={group.label} group={group} />
        ))}
      </nav>

      <IdentityFooter />
    </aside>
  );
}

function BrandMark() {
  // Small flame-on-square brand mark. Drawn with the accent token so it
  // re-tints with the theme. Keeping it inline avoids an asset round-trip.
  return (
    <span
      className="inline-flex size-8 items-center justify-center rounded-md text-[var(--color-accent-contrast)]"
      style={{ backgroundColor: "var(--color-accent)" }}
      aria-hidden
    >
      <svg viewBox="0 0 20 20" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 3c1 3 4 4 4 8a4 4 0 1 1-8 0c0-2 1-2 1-4 1 1 2 1 3-4Z" fill="currentColor" />
      </svg>
    </span>
  );
}

/**
 * Active-link match. We DON'T use NavLink's default prefix-based
 * `isActive` because it highlights both `/portfolio` and
 * `/portfolio/rebalance` simultaneously when on the latter — we want
 * exact-pathname match so only ONE nav item is active. (Hash-anchor
 * sidebar entries existed pre-Phase-13.5 and required a hash-aware
 * comparator; those entries are gone now.)
 */
function isNavLinkActive(pathname: string, to: string): boolean {
  return pathname === to;
}

function NavGroupBlock({ group }: { group: NavGroup }) {
  const location = useLocation();
  return (
    <div className="mb-4">
      <div className="mb-1 flex items-center gap-2 px-3 text-xs font-medium uppercase tracking-wide text-[var(--color-faint)]">
        <span className="opacity-70">{group.icon}</span>
        <span>{group.label}</span>
      </div>
      <ul className="space-y-0.5">
        {group.items.map((item) => {
          const active = item.to
            ? isNavLinkActive(location.pathname, item.to)
            : false;
          return (
          <li key={item.label}>
            {item.to ? (
              <NavLink
                to={item.to}
                className={[
                  "flex items-center justify-between rounded-md px-3 py-1.5 text-sm transition-colors",
                  active
                    ? "bg-[var(--color-accent-soft)] font-medium text-[var(--color-accent)]"
                    : "text-[var(--color-text)] hover:bg-[var(--color-elevated)]",
                ].join(" ")}
                end
              >
                <span>{item.label}</span>
              </NavLink>
            ) : (
              <div
                className="flex cursor-not-allowed items-center justify-between rounded-md px-3 py-1.5 text-sm italic text-[var(--color-faint)]"
                aria-disabled
                title={`Coming in ${item.comingPhase ?? "a future phase"}`}
              >
                <span>{item.label}</span>
                <span className="text-[10px] uppercase tracking-wide">
                  {item.comingPhase ?? "soon"}
                </span>
              </div>
            )}
          </li>
          );
        })}
      </ul>
    </div>
  );
}

function IdentityFooter() {
  const { user, signOut } = useAuth();
  const {
    theme,
    toggleTheme,
    privacyMode,
    togglePrivacyMode,
    simpleView,
    toggleSimpleView,
  } = usePreferences();
  const navigate = useNavigate();

  return (
    <div className="border-t border-[var(--color-border-default)] p-3">
      {/* Identity card */}
      <div className="mb-3 rounded-lg bg-[var(--color-elevated)] p-3">
        <p className="text-xs font-medium text-[var(--color-text)]">
          {user?.display_name ?? "Signed out"}
        </p>
        <p className="text-[11px] text-[var(--color-muted)]">
          {user?.role ? roleLabel(user.role) : "—"} · Manhattan, NY
        </p>
      </div>

      {/* Three-toggle row — theme, privacy, Simple View. */}
      <div className="mb-3 flex gap-1">
        <FooterToggle
          active={theme === "dark"}
          onClick={toggleTheme}
          title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          ariaLabel="Toggle theme"
        >
          {theme === "dark" ? <SunIcon /> : <MoonIcon />}
        </FooterToggle>
        <FooterToggle
          active={privacyMode}
          onClick={togglePrivacyMode}
          title={privacyMode ? "Show dollar values" : "Hide dollar values"}
          ariaLabel="Toggle privacy mode"
        >
          {privacyMode ? <EyeOffIcon /> : <EyeIcon />}
        </FooterToggle>
        <FooterToggle
          active={simpleView}
          onClick={toggleSimpleView}
          title={simpleView ? "Show full view" : "Show simple view"}
          ariaLabel="Toggle simple view"
        >
          <SimpleIcon />
        </FooterToggle>
      </div>

      <div className="flex gap-2">
        <AppShellSettingsTray />
        <button
          type="button"
          onClick={() => {
            void signOut().then(() => navigate("/signin", { replace: true }));
          }}
          className="flex-1 rounded-md border border-[var(--color-border-default)] px-2 py-1.5 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-elevated)]"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

function FooterToggle({
  active,
  onClick,
  title,
  ariaLabel,
  children,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  ariaLabel: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={ariaLabel}
      aria-pressed={active}
      className={[
        "flex flex-1 items-center justify-center rounded-md border px-2 py-1.5 transition-colors",
        active
          ? "border-transparent bg-[var(--color-accent)] text-[var(--color-accent-contrast)]"
          : "border-[var(--color-border-default)] text-[var(--color-text)] hover:bg-[var(--color-elevated)]",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function roleLabel(role: string): string {
  switch (role) {
    case "owner": return "Owner";
    case "admin": return "Admin";
    case "member": return "Member";
    case "viewer": return "Viewer";
    case "auditor": return "Auditor";
    case "advisor": return "Advisor";
    default: return role;
  }
}

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.5 5.5l1.4 1.4M17.1 17.1l1.4 1.4M5.5 18.5l1.4-1.4M17.1 6.9l1.4-1.4" />
    </svg>
  );
}
function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
    </svg>
  );
}
function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function EyeOffIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="m3 3 18 18M10.6 6.2A9.4 9.4 0 0 1 12 6c6.5 0 10 7 10 7a14.6 14.6 0 0 1-3.3 3.9M6.6 6.6C3.6 8.6 2 12 2 12s3.5 7 10 7c1.6 0 3-.3 4.2-.8" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    </svg>
  );
}
function SimpleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="6" rx="1.5" />
      <rect x="3" y="13" width="11" height="6" rx="1.5" />
    </svg>
  );
}

export type { Theme };
